import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ChefHat, Facebook } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { api, saveSessionToken } from '../lib/api';
import { showBanner, hideBanner, showInterstitialNow, prepareInterstitial } from '../lib/admob';

// Initialize Google Auth once on app start (idempotent — plugin handles re-init).
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
let _googleAuthInitialised = false;
async function ensureGoogleAuthInit() {
  if (_googleAuthInitialised) return;
  try {
    await GoogleAuth.initialize({
      clientId: '83055319127-vu0ohkp9ihdoc770v8pvg29c53vjv7vn.apps.googleusercontent.com',
      scopes: ['profile', 'email'],
      grantOfflineAccess: false,
    });
    _googleAuthInitialised = true;
  } catch (e) {
    console.warn('[login] GoogleAuth.initialize failed', e);
  }
}

export default function Login() {
  const { t } = useLang();
  const { user, loading, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // ─── Banner ad lifecycle: visible only while the login screen is mounted ───
  // Since the user is NOT logged in yet, we don't know if they're premium —
  // banner is shown unconditionally. It auto-hides the moment we navigate away.
  // NOTE: this hook MUST be declared before the early-return below or React
  // will complain about hook order.
  useEffect(() => {
    showBanner().catch(() => {});
    // Preload an interstitial in the background so it shows instantly post-login.
    prepareInterstitial().catch(() => {});
    return () => { hideBanner().catch(() => {}); };
  }, []);

  if (!loading && user) {
    const dest = location.state?.from?.pathname || '/';
    return <Navigate to={dest} replace />;
  }

  const handleLogin = async () => {
    const isNative = Capacitor.isNativePlatform?.();
    const isAndroid = isNative && Capacitor.getPlatform?.() === 'android';

    // ────── Native Android: one-tap Google account picker ──────
    if (isAndroid) {
      try {
        await ensureGoogleAuthInit();
        const result = await GoogleAuth.signIn();
        const idToken = result?.authentication?.idToken;
        if (!idToken) throw new Error('No idToken returned');

        const res = await api.post('/auth/google-native', { idToken });
        const sessionToken = res?.data?.session_token;
        if (sessionToken) saveSessionToken(sessionToken);
        await refresh();
        // Tear down the banner BEFORE leaving the login screen — `await` is
        // critical here, otherwise the banner can race the route change and
        // remain visible on the next page (which is what the user reported).
        await hideBanner().catch(() => {});
        // Welcome interstitial — fire-and-forget so navigation isn't blocked.
        showInterstitialNow().catch(() => {});
        const dest = location.state?.from?.pathname || '/';
        navigate(dest, { replace: true });
        return;
      } catch (e) {
        const code = e?.code || e?.error || '';
        if (code === '12501' || /cancel/i.test(String(e?.message || e?.error || ''))) {
          return; // user dismissed the picker — nothing to do
        }
        console.warn('[login] Native Google Sign-In failed, falling back to web flow', e);
        toast.info('سيتم استخدام طريقة تسجيل الدخول البديلة...');
      }
    }

    // ────── Web (and Android fallback): Emergent OAuth via Chrome Custom Tab ──────
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = isNative
      ? 'https://cook-smart-40.emergent.host/'
      : window.location.origin + '/';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    if (isNative) {
      try {
        await Browser.open({
          url: authUrl,
          presentationStyle: 'popover',
          windowName: '_self',
        });
      } catch (e) {
        console.error('[login] Browser.open failed', e);
        window.location.href = authUrl;
      }
    } else {
      window.location.href = authUrl;
    }
  };

  const notReady = () => toast.info(t('comingSoon'));

  return (
    <div className="min-h-[80vh] flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md mx-auto bg-white dark:bg-neutral-900 rounded-3xl border border-black/5 dark:border-white/10 shadow-sm p-8 sm:p-10 text-center space-y-6">
        <div className="relative inline-flex">
          <div className="absolute inset-0 rounded-3xl bg-[#C05A42]/30 blur-2xl animate-pulse" />
          <div className="relative inline-flex h-20 w-20 items-center justify-center rounded-3xl bg-[#C05A42] text-white mx-auto shadow-lg">
            <ChefHat className="h-10 w-10" />
          </div>
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">{t('appName')}</h1>
          <p className="mt-1 text-[#5C4D44] dark:text-neutral-400 text-sm">{t('tagline')}</p>
          <p className="mt-4 text-[#2C2420] dark:text-neutral-200 leading-relaxed font-medium">{t('welcomeBack')}</p>
          <p className="text-[#5C4D44] dark:text-neutral-400 text-sm">{t('loginDesc')}</p>
        </div>

        <button
          onClick={handleLogin}
          data-testid="google-login-btn"
          className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#2C2420] hover:bg-black dark:bg-white dark:text-[#2C2420] dark:hover:bg-neutral-200 text-white px-6 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-sm"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.7 5.9 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.4-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.7 6.9 29.1 5 24 5 16.3 5 9.7 9.1 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.2C29.1 35.7 26.7 36.5 24 36.5c-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.6 39.8 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.2C40.9 35.3 44 30.1 44 24c0-1.4-.1-2.4-.4-3.5z"/>
          </svg>
          {t('loginWithGoogle')}
        </button>

        <div className="relative flex items-center text-xs text-[#5C4D44] dark:text-neutral-500">
          <span className="flex-1 border-t border-black/5 dark:border-white/10" />
          <span className="px-3 uppercase tracking-wider">{t('or')}</span>
          <span className="flex-1 border-t border-black/5 dark:border-white/10" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={notReady}
            data-testid="facebook-login-btn"
            className="relative inline-flex items-center justify-center gap-2 rounded-full bg-[#1877F2] text-white px-4 py-3 font-medium opacity-70 cursor-not-allowed"
            disabled
          >
            <Facebook className="h-4 w-4" />
            <span className="text-xs">Facebook</span>
            <span className="absolute -top-1.5 -end-1.5 bg-[#E29F48] text-white text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full">{t('comingSoon')}</span>
          </button>
          <button
            onClick={notReady}
            data-testid="x-login-btn"
            className="relative inline-flex items-center justify-center gap-2 rounded-full bg-black text-white px-4 py-3 font-medium opacity-70 cursor-not-allowed"
            disabled
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            <span className="text-xs">X</span>
            <span className="absolute -top-1.5 -end-1.5 bg-[#E29F48] text-white text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full">{t('comingSoon')}</span>
          </button>
        </div>

        <p className="text-[10px] text-[#5C4D44] dark:text-neutral-500 leading-relaxed">
          {t('privacyPolicy')} · <a href="/privacy" className="underline hover:text-[#C05A42]">{t('privacyPolicy')}</a>
        </p>
      </div>
    </div>
  );
}
